import React from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ConfirmationModal, IConfirmationModalProps } from 'components/confirmation-modal';
import HeroHeading from 'components/hero-heading';
import MenuSectionHeading from 'components/menu-section-heading';
import Picture from 'components/picture';
import { IOurServicesFragment } from 'generated/sanity-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useOffersContext } from 'state/offers';
import { ServiceMode, useOrderContext } from 'state/order';
import { IOrder } from 'state/order/types';
import { useStoreContext } from 'state/store';
import { handleCateringClick, handleConfirm } from 'utils/catering';
import { routes } from 'utils/routing';

import OurServicesCard from './our-services-card';

const Section = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem;
  margin-top: -8rem;
  position: relative;

  ${Styles.desktop`
    grid-template-columns: 1fr 1fr 1fr;
    margin-left: auto;
    margin-right: auto;
    max-width: ${Styles.layout.sectionMaxWidth};
    padding-bottom: 6rem;
    padding-top: 0;
    width: calc(100% - 6rem);
  `}

  ${Styles.mobile`
    padding: 0 3rem 1.25rem;
  `}
`;

const StyledHeroHeading = styled(HeroHeading)`
  color: ${Styles.color.black};
  margin-bottom: 1rem;
  margin-left: auto;
  margin-right: auto;
  max-width: ${Styles.layout.sectionMaxWidth};
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5rem 2rem 10rem;
  width: 100%;

  ${Styles.desktop`
    padding: 5rem 8rem 10rem;
  `}
`;

const OurServicesSection: React.FC<{
  item: IOurServicesFragment;
}> = ({ item: data }) => {
  const ourServicesHeroImageLocale = data?.ourServicesHeroImageLocale?.locale;
  const servicesHeroTextLocale = data?.servicesHeroTextLocale?.locale;
  const servicesHeaderTextLocale = data?.servicesHeaderTextLocale?.locale;
  const order: IOrder = useOrderContext();
  const { isStoreOpenAndAvailable, resetStore } = useStoreContext();
  const { clearSelectedOffer, selectedOffer } = useOffersContext();
  const { formatMessage } = useIntl();
  const mParticle = useMParticleContext();
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const disableDriveThru = useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const disableDineIn = useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const disableTakeOut = useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);
  const navigate = useNavigate();

  const disablePickup = disableDriveThru && disableDineIn && disableTakeOut;
  const [ConfirmationDialog, showConfirmationDialog] = useDialogModal<{}, IConfirmationModalProps>({
    Component: ConfirmationModal,
  });

  const handleCateringOrderNowClick = () => {
    mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_CATERING);
    handleCateringClick({
      cartEntries: order.cartEntries,
      isCatering: order.isCatering,
      selectedOffer,
      showConfirmationDialog,
      isStoreOpenAndAvailable,
      navigate,
    });
  };

  return (
    <>
      <MenuSectionHeading>{servicesHeaderTextLocale}</MenuSectionHeading>
      {ourServicesHeroImageLocale && servicesHeroTextLocale && (
        <Picture image={ourServicesHeroImageLocale} alt="">
          <HeroContent>
            <StyledHeroHeading
              as="h2"
              dangerouslySetInnerHTML={{ __html: servicesHeroTextLocale }}
            />
          </HeroContent>
        </Picture>
      )}
      <Section>
        <OurServicesCard
          icon={<Icon icon="cart" color="icon-active" height="100px" aria-hidden />}
          title="Pick&nbsp;Up"
          testId="pick-up-card"
          description="Save time, order online and pick up your order seamlessly in store."
          actionText={
            disablePickup ? formatMessage({ id: 'comingSoon' }) : formatMessage({ id: 'orderNow' })
          }
          disabled={disablePickup}
          onClick={() => {
            order.selectServiceMode(ServiceMode.DRIVE_THRU);
            navigate(routes.storeLocator);
          }}
        />
        <OurServicesCard
          icon={<Icon icon="delivery" color="icon-active" height="100px" aria-hidden />}
          title="Delivery"
          testId="delivery-card"
          description="Get your order delivered to your home or office. Quick and convenient."
          actionText={
            enableDelivery ? formatMessage({ id: 'orderNow' }) : formatMessage({ id: 'comingSoon' })
          }
          disabled={!enableDelivery}
          onClick={() => {
            order.selectServiceMode(ServiceMode.DELIVERY);
            navigate(routes.address);
          }}
        />
        <OurServicesCard
          icon={<Icon icon="catering" color="icon-active" height="100px" aria-hidden />}
          title="Catering"
          testId="catering-card"
          description="We make it easy to cater your next event."
          actionText={
            enableCatering ? formatMessage({ id: 'orderNow' }) : formatMessage({ id: 'comingSoon' })
          }
          disabled={!enableCatering}
          onClick={handleCateringOrderNowClick}
        />
        <ConfirmationDialog
          headingText={formatMessage({ id: 'quickNote' })}
          bodyText={formatMessage({ id: 'yourCartWillBeClearedIfStartCateringOrder' })}
          onConfirm={() => handleConfirm({ clearSelectedOffer, navigate, resetStore, ...order })}
          modalAppearanceEventMessage="Confirmation: Selecting catering will clear cart"
        />
      </Section>
    </>
  );
};

export default OurServicesSection;
